import { Link } from 'react-router-dom';

const features = [
  {
    icon: 'ri-flashlight-fill',
    color: 'bg-orange-50 text-orange-500',
    title: 'Complete Ads in Seconds',
    description: 'Fill a short form and get fully written hooks, headlines, body copy, CTAs — plus AI-generated creative images — all formatted for your chosen platform.',
    isNew: false,
  },
  {
    icon: 'ri-sparkling-2-fill',
    color: 'bg-amber-50 text-amber-500',
    title: 'Social Posts Generator',
    description: 'Generate platform-native organic content for Facebook, Instagram, LinkedIn, X, TikTok, and Google Business Profile — captions, hooks, CTAs, hashtags, and visual direction in one shot.',
    isNew: true,
    link: '/social-quick-start',
  },
  {
    icon: 'ri-magic-line',
    color: 'bg-amber-50 text-amber-600',
    title: 'AI-Generated Creative Images',
    description: 'Every generation includes real AI-produced ad visuals — not stock photos. Free plan gets 1 image per generation, Pro gets 3, and Agency gets 6.',
    isNew: false,
  },
  {
    icon: 'ri-edit-box-line',
    color: 'bg-rose-50 text-rose-500',
    title: 'Visual Ad Editor',
    description: 'Open any generated ad in the built-in editor. Drag elements, edit text inline, customize buttons, adjust colors, and fine-tune animations — all without leaving AdForge.',
    isNew: false,
  },
  {
    icon: 'ri-compass-3-line',
    color: 'bg-amber-50 text-amber-600',
    title: 'Ad Angle & Strategy Controls',
    description: 'Choose your campaign goal and ad angle — Problem/Solution, Before/After, Offer-driven, Emotional, or Authority — or let the AI pick the best approach.',
    isNew: false,
  },
  {
    icon: 'ri-save-line',
    color: 'bg-rose-50 text-rose-500',
    title: 'Save, Edit & Organize',
    description: 'Keep a library of your best ads. Edit individual images without re-generating everything. Export, copy with one click, or share with your team.',
    isNew: false,
  },
];

export default function Features() {
  return (
    <section id="features" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-semibold text-orange-500 uppercase tracking-widest mb-3">Why AdForge</span>
          <h2 className="text-4xl md:text-5xl font-extrabold text-stone-900 mb-4">
            Ads <em>and</em> social content —
            <br />
            <span className="text-orange-500">all in one platform</span>
          </h2>
          <p className="text-lg text-stone-500 max-w-2xl mx-auto">
            Stop wasting time on copy and creative. AdForge AI writes your paid ads, generates images, creates organic social posts, and gives you a visual editor to fine-tune everything.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className={`relative bg-white rounded-2xl p-7 border transition-all duration-300 cursor-default ${feature.isNew ? 'border-amber-200 hover:border-amber-300 ring-1 ring-amber-100' : 'border-stone-100 hover:border-orange-200'}`}
            >
              {feature.isNew && (
                <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-widest bg-gradient-to-r from-orange-500 to-amber-400 text-white px-2.5 py-1 rounded-full">
                  New
                </span>
              )}
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl mb-5 ${feature.color}`}>
                <i className={`${feature.icon} text-xl`} />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">{feature.title}</h3>
              <p className="text-stone-500 text-sm leading-relaxed mb-3">{feature.description}</p>
              {feature.link && (
                <Link
                  to={feature.link}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-orange-500 hover:text-orange-600 transition-colors duration-200 group"
                >
                  Try Social Posts
                  <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
              )}
            </div>
          ))}
        </div>

        {/* Internal SEO link */}
        <div className="mt-12 text-center">
          <Link
            to="/ai-ad-generator"
            className="inline-flex items-center gap-2 text-sm font-semibold text-orange-500 hover:text-orange-600 transition-colors duration-200 group"
          >
            <i className="ri-information-line text-base" />
            See the full breakdown of our AI ad generator — features, use cases &amp; how it works
            <i className="ri-arrow-right-line text-base group-hover:translate-x-0.5 transition-transform duration-200" />
          </Link>
          <span className="text-stone-300 mx-3">·</span>
          <Link
            to="/how-it-works"
            className="inline-flex items-center gap-2 text-sm font-semibold text-stone-400 hover:text-orange-500 transition-colors duration-200 group"
          >
            See how it works step by step
            <i className="ri-arrow-right-line text-base group-hover:translate-x-0.5 transition-transform duration-200" />
          </Link>
        </div>
      </div>
    </section>
  );
}
